"use client";

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { FiActivity } from "react-icons/fi";
import HorizontalScroll from "@/app/components/HorizontalScroll";
import { useTheme } from "next-themes";
import {
  createChart,
  ColorType,
  CrosshairMode,
  LineSeries,
  LastPriceAnimationMode,
  LineType,
} from "lightweight-charts";
import type { IChartApi, ISeriesApi, Time } from "lightweight-charts";
import dynamic from "next/dynamic";
import { useT } from "@/hooks/useT";
import { formatCents } from '@/lib/format-cents';

const KalshiVisxChart = dynamic(() => import("./KalshiVisxChart"), { ssr: false });

interface Market {
  id: string;
  question: string;
  groupItemTitle?: string | null;
  slug: string;
  outcomes: string[];
  outcomePrices: number[];
  clobTokenIds: string[];
  volume: number;
}

interface EventMultiOutcomeChartProps {
  markets: Market[];
  eventTitle: string;
  source?: 'polymarket' | 'kalshi' | 'predict' | 'hyperliquid';
  prefetchedData?: Map<string, any[]> | null;
  eventSlug?: string;
  /** DFlow real-time prices for Kalshi markets (for forecast line) */
  realtimePrices?: Map<string, { yesBid: number | null; yesAsk: number | null }>;
  /** Event-level total volume (USD). Renders below the chart next to the
   * close date + rewards badge — mirrors Polymarket's meta-footer. */
  eventVolume?: number;
  /** Event close / resolution date (ISO). Shown as "Nov 7, 2028" etc. */
  eventEndDate?: string | null;
  /** When true, renders the "Earn 4%" Holding Rewards badge with a
   * tooltip + link to the help center. Polymarket gates this per-event
   * on `markets[i].holdingRewardsEnabled`; compute the OR upstream. */
  hasHoldingRewards?: boolean;
}

// ─── Price-level event detection ────────────────────────────────────────────

interface PriceLevelInfo {
  isPriceLevel: true;
  baseStrike: number;
  strikes: { marketIndex: number; strike: number }[];
  hasDollarSign: boolean;
}

type PriceLevelResult = PriceLevelInfo | { isPriceLevel: false };

const STRIKE_REGEX = /(?:\$\s*)([\d,]+(?:\.\d+)?)\s*(?:or\s+)?(?:above|over|more|higher)/i;
const STRIKE_REGEX_NO_DOLLAR = /([\d,]+(?:\.\d+)?)\s*(?:or\s+)?(?:above|over|more|higher)/i;

// 2026-05-26: weather events (NYC/London/Tokyo temperature ranges) have
// the same intra-day swings as crypto strikes but live on Polymarket
// instead of Kalshi, so detectPriceLevelEvent (kalshi-only) doesn't
// catch them. Heuristic: any group title or event title with a temp
// unit (°C / °F / "degrees") + a numeric range is weather. The chart
// default switches to 6H for these so the user sees the last few
// hours of action instead of a flat all-time line.
const WEATHER_UNIT_REGEX = /°\s*[CF]\b|\bdegrees?\s+(?:celsius|fahrenheit|c|f)\b/i;
function detectWeatherEvent(markets: Market[], eventTitle?: string): boolean {
  if (eventTitle && WEATHER_UNIT_REGEX.test(eventTitle)) return true;
  let hits = 0;
  for (const m of markets) {
    const text = `${m.groupItemTitle || ''} ${m.question || ''}`;
    if (WEATHER_UNIT_REGEX.test(text)) hits++;
    if (hits >= 2) return true; // 2+ rows with units => confident it's weather
  }
  return false;
}

function detectPriceLevelEvent(markets: Market[], source?: string): PriceLevelResult {
  if (source !== 'kalshi' || markets.length < 5) return { isPriceLevel: false };

  const strikes: { marketIndex: number; strike: number; hasDollar: boolean }[] = [];

  for (let i = 0; i < markets.length; i++) {
    const text = markets[i].groupItemTitle || markets[i].question || '';
    const match = text.match(STRIKE_REGEX) || text.match(STRIKE_REGEX_NO_DOLLAR);
    if (match) {
      strikes.push({
        marketIndex: i,
        strike: parseFloat(match[1].replace(/,/g, '')),
        hasDollar: STRIKE_REGEX.test(text),
      });
    }
  }

  if (strikes.length < markets.length * 0.6) return { isPriceLevel: false };

  strikes.sort((a, b) => a.strike - b.strike);
  return {
    isPriceLevel: true,
    baseStrike: strikes[0].strike,
    strikes: strikes.map(({ marketIndex, strike }) => ({ marketIndex, strike })),
    hasDollarSign: strikes[0].hasDollar,
  };
}

// ─── Prefetch cache ─────────────────────────────────────────────────────────

const prefetchCache = new Map<string, Promise<Record<string, any[]>>>();

export function prefetchPriceHistory(
  markets: { id: string; clobTokenIds: string[] }[],
  source: 'polymarket' | 'kalshi' | 'predict' = 'polymarket',
): void {
  if (typeof window === 'undefined') return;
  if (!markets.length) return;

  // Predict.fun has no price history API — skip prefetch
  if (source === 'predict') return;

  const cacheKey = source === 'kalshi'
    ? `kalshi:${markets.map(m => m.id).join(',')}`
    : `pm:${markets.map(m => m.clobTokenIds?.[0]).filter(Boolean).join(',')}`;

  if (prefetchCache.has(cacheKey)) return;

  const promise = (async () => {
    if (source === 'kalshi') {
      const tickers = markets.map(m => m.id).filter(Boolean);
      if (!tickers.length) return {};
      const now = Math.floor(Date.now() / 1000);
      const res = await fetch('/api/markets/kalshi/candlesticks/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tickers: tickers.slice(0, 20), period: 1440, startTs: now - 31536000, endTs: now }),
      });
      if (!res.ok) return {};
      const data = await res.json();
      return data.results || {};
    }

    const tokenIds = markets.map(m => m.clobTokenIds?.[0]).filter((id): id is string => !!id);
    if (!tokenIds.length) return {};
    const BATCH_LIMIT = 50;
    const chunks: string[][] = [];
    for (let i = 0; i < tokenIds.length; i += BATCH_LIMIT) {
      chunks.push(tokenIds.slice(i, i + BATCH_LIMIT));
    }
    const results = await Promise.all(
      chunks.map(async (chunk) => {
        const res = await fetch('/api/polymarket/prices-history-batch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tokenIds: chunk, interval: 'max', fidelity: '720' }),
        });
        if (!res.ok) return {};
        const data = await res.json();
        return data.data?.histories || {};
      })
    );
    const merged: Record<string, any[]> = {};
    for (const r of results) Object.assign(merged, r);
    return merged;
  })();

  prefetchCache.set(cacheKey, promise);
  setTimeout(() => prefetchCache.delete(cacheKey), 60_000);
}

function getPrefetchCacheKey(
  markets: { id: string; clobTokenIds?: string[] }[],
  source: 'polymarket' | 'kalshi' | 'predict',
): string {
  return source === 'kalshi' || source === 'predict'
    ? `kalshi:${markets.map(m => m.id).join(',')}`
    : `pm:${markets.map(m => m.clobTokenIds?.[0]).filter(Boolean).join(',')}`;
}

// ─── Constants ──────────────────────────────────────────────────────────────

type TimeFilter = "1H" | "6H" | "1D" | "1W" | "1M" | "ALL";

// Endpoint params match Polymarket's own UI exactly (verified via their
// XHR traces). Using the native interval=1m for the 1M filter means the
// CLOB returns only last 30 days of data instead of us fetching everything
// and filtering client-side.
const TIME_FILTERS: { label: TimeFilter; interval: string; fidelity: string }[] = [
  { label: "1H", interval: "1h", fidelity: "1" },
  { label: "6H", interval: "6h", fidelity: "1" },
  { label: "1D", interval: "1d", fidelity: "5" },
  { label: "1W", interval: "1w", fidelity: "30" },
  { label: "1M", interval: "1m", fidelity: "180" },
  { label: "ALL", interval: "max", fidelity: "720" },
];

// Kalshi limit: 5000 candlesticks per request. Period must be chosen so
// duration / period_minutes ≤ 5000. e.g. 1 year hourly = 8760 → exceeds limit.
// ALL uses a 10y window (3650 daily candles) to cover long-dated election
// markets whose tickers can pre-date the 1-year cutoff — matches kalshi.com.
const KALSHI_TIME_MAP: Record<TimeFilter, { period: number; duration: number }> = {
  "1H":  { period: 1,    duration: 3600 },
  "6H":  { period: 1,    duration: 21600 },
  "1D":  { period: 60,   duration: 86400 },
  "1W":  { period: 60,   duration: 604800 },
  "1M":  { period: 1440, duration: 2592000 },
  "ALL": { period: 1440, duration: 157680000 },
};

// Premium color palette — distinct, vibrant, high-contrast
const OUTCOME_COLORS = [
  "#3B82F6", // Blue
  "#F59E0B", // Amber
  "#8B5CF6", // Violet
  "#06B6D4", // Cyan
  "#EC4899", // Pink
  "#10B981", // Emerald
  "#F97316", // Orange
  "#6366F1", // Indigo
];

const formatVolume = (vol: number | null | undefined) => {
  if (vol == null) return "$0";
  if (vol >= 1_000_000) return `$${(vol / 1_000_000).toFixed(1)}M`;
  if (vol >= 1_000) return `$${(vol / 1_000).toFixed(1)}K`;
  return `$${vol.toFixed(0)}`;
};

const toSafeNumber = (value: unknown, fallback = 0): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const COLLAPSED_LIMIT_DESKTOP = 4;
const COLLAPSED_LIMIT_MOBILE = 3;
const CHART_HEIGHT = 200;
const FETCH_TIMEOUT = 15_000;

function getShortLabel(market: Market): string {
  if (market.groupItemTitle) return market.groupItemTitle;
  const question = market.question;
  if (question.length <= 15) return question;
  const willWinMatch = question.match(/^Will\s+(.+?)\s+win\s+the\s+/i);
  if (willWinMatch) return willWinMatch[1].replace(/\s*(FC|CF|SC|AC|United|City)$/i, '').trim();
  const willMatch = question.match(/^Will\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+(?:win|be|become|get|receive)/i);
  if (willMatch) return willMatch[1];
  const rangeMatch = question.match(/(?<!\d)((?!(?:19|20)\d{2})\d+[-–]\d+)/);
  if (rangeMatch) return rangeMatch[1];
  const singleNumberMatch = question.match(/(\d{2,})\s*\+|under\s*(\d{2,})|over\s*(\d{2,})|exactly\s*(\d{2,})/i);
  if (singleNumberMatch) {
    const num = singleNumberMatch[1] || singleNumberMatch[2] || singleNumberMatch[3] || singleNumberMatch[4];
    if (singleNumberMatch[0].includes('+')) return `${num}+`;
    if (singleNumberMatch[0].toLowerCase().includes('under')) return `<${num}`;
    if (singleNumberMatch[0].toLowerCase().includes('over')) return `>${num}`;
    return num;
  }
  return question.substring(0, 12) + "...";
}

async function fetchWithTimeout(url: string, opts: RequestInit, timeoutMs = FETCH_TIMEOUT): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...opts, signal: controller.signal });
  } finally {
    clearTimeout(id);
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function EventMultiOutcomeChart({
  markets,
  eventTitle,
  source = 'polymarket',
  eventSlug,
  realtimePrices,
  defaultTimeFilter,
  eventVolume,
  eventEndDate,
  hasHoldingRewards,
}: EventMultiOutcomeChartProps & { defaultTimeFilter?: TimeFilter }) {
  // Predict sports: expand a single market with N outcomes into N virtual markets (one line per outcome)
  const effectiveMarkets = useMemo(() => {
    if (source !== 'predict' || markets.length !== 1) return markets;
    const m = markets[0];
    if (!m.outcomes || m.outcomes.length < 2) return markets;
    return m.outcomes.map((name, i) => ({
      ...m,
      id: `${m.id}__outcome_${i}`,
      question: name,
      groupItemTitle: name,
      outcomePrices: [m.outcomePrices?.[i] ?? 0],
    }));
  }, [markets, source]);

  const priceLevelInfo = useMemo(() => detectPriceLevelEvent(effectiveMarkets, source), [effectiveMarkets, source]);
  const isForecast = priceLevelInfo.isPriceLevel;
  const isWeather = useMemo(() => detectWeatherEvent(effectiveMarkets, eventTitle), [effectiveMarkets, eventTitle]);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  const COLLAPSED_LIMIT = isMobile ? COLLAPSED_LIMIT_MOBILE : COLLAPSED_LIMIT_DESKTOP;
  const [chartData, setChartData] = useState<any[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>(
    defaultTimeFilter || (isWeather ? "6H" : isForecast ? "1D" : "ALL"),
  );
  const [visibleMarkets, setVisibleMarkets] = useState<Set<number>>(new Set());
  const [isLegendExpanded, setIsLegendExpanded] = useState(false);

  // Drag-to-scroll for legend pills
  const pillsRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const scrollStartX = useRef(0);
  const handlePillsMouseDown = useCallback((e: React.MouseEvent) => {
    const el = pillsRef.current;
    if (!el) return;
    isDragging.current = true;
    dragStartX.current = e.clientX;
    scrollStartX.current = el.scrollLeft;
    el.style.cursor = 'grabbing';
  }, []);
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current || !pillsRef.current) return;
      e.preventDefault();
      pillsRef.current.scrollLeft = scrollStartX.current - (e.clientX - dragStartX.current);
    };
    const handleMouseUp = () => {
      isDragging.current = false;
      if (pillsRef.current) pillsRef.current.style.cursor = '';
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => { document.removeEventListener('mousemove', handleMouseMove); document.removeEventListener('mouseup', handleMouseUp); };
  }, []);

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRefs = useRef<Map<number, ISeriesApi<"Line">>>(new Map());
  const abortRef = useRef<AbortController | null>(null);
  const hasLoadedOnce = useRef(false);
  // PM-style hover overlay: vertical dashed line (from the chart library) +
  // per-series colored pills anchored to each line's y-pixel at the crosshair
  // + a date label at the top + a subtle gray overlay over the "future" side.
  // Each pill is keyed to the series index so the renderer can place it at
  // the exact pixel intersection of the cursor X and that series' line.
  const [hoverState, setHoverState] = useState<{
    x: number; // cursor pixel X inside the chart container
    time: number; // unix seconds
    items: Array<{ idx: number; y: number; name: string; color: string; value: number }>;
  } | null>(null);
  // Mirror `effectiveMarkets` + `visibleMarkets` + `chartData` into refs so
  // the crosshair handler (bound once when the chart is created) always
  // reads the latest values without re-subscribing on every state change.
  const effectiveMarketsRef = useRef(effectiveMarkets);
  const visibleMarketsRef = useRef(visibleMarkets);
  const chartDataRef = useRef<any[]>([]);
  useEffect(() => { effectiveMarketsRef.current = effectiveMarkets; }, [effectiveMarkets]);
  useEffect(() => { visibleMarketsRef.current = visibleMarkets; }, [visibleMarkets]);
  useEffect(() => { chartDataRef.current = chartData; }, [chartData]);
  const t = useT();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Market indices in source order (already sorted chronologically by EventClient)
  const orderedMarketIndices = useMemo(() => {
    return effectiveMarkets.map((_, i) => i);
  }, [effectiveMarkets]);

  const displayedIndices = useMemo(() => {
    if (isLegendExpanded || effectiveMarkets.length <= COLLAPSED_LIMIT) return orderedMarketIndices;
    return orderedMarketIndices.slice(0, COLLAPSED_LIMIT);
  }, [orderedMarketIndices, isLegendExpanded, effectiveMarkets.length, COLLAPSED_LIMIT]);

  const hiddenCount = effectiveMarkets.length - COLLAPSED_LIMIT;

  // Initialize visible markets to exactly match the collapsed legend pills
  // (COLLAPSED_LIMIT = 4 on desktop / 3 on mobile). Hardcoding 5 was drawing
  // an extra line with no pill for the user to toggle.
  useEffect(() => {
    if (effectiveMarkets.length > 0) {
      const indices = effectiveMarkets
        .map((_, i) => i)
        .slice(0, COLLAPSED_LIMIT);
      setVisibleMarkets(new Set(indices));
    }
  }, [effectiveMarkets, COLLAPSED_LIMIT]);

  const totalVolume = useMemo(() => {
    return effectiveMarkets.reduce((sum, m) => sum + (m.volume || 0), 0);
  }, [effectiveMarkets]);

  // ─── Live forecast from DFlow WS prices ──────────────────────────────

  const liveForecast = useMemo(() => {
    if (!priceLevelInfo.isPriceLevel || !realtimePrices?.size) return null;
    let sum = 0;
    for (const { marketIndex } of priceLevelInfo.strikes) {
      const market = markets[marketIndex];
      const live = realtimePrices.get(market.slug);
      if (live) {
        const mid = (live.yesBid != null && live.yesAsk != null)
          ? (live.yesBid + live.yesAsk) / 2
          : (live.yesBid ?? live.yesAsk ?? market.outcomePrices[0]);
        sum += mid;
      } else {
        sum += market.outcomePrices[0]; // fallback to static price
      }
    }
    return priceLevelInfo.baseStrike + sum;
  }, [priceLevelInfo, realtimePrices, markets]);

  // Append live forecast point to chart
  useEffect(() => {
    if (!isForecast || liveForecast == null) return;
    const series = seriesRefs.current.get(0);
    if (series) {
      series.update({ time: Math.floor(Date.now() / 1000) as Time, value: liveForecast });
    }
  }, [isForecast, liveForecast]);

  // ─── Data fetching ────────────────────────────────────────────────────

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    async function fetchAllPriceHistories() {
      if (!markets.length) { setInitialLoading(false); return; }

      if (hasLoadedOnce.current) setUpdating(true);

      try {
        const prefetchKey = getPrefetchCacheKey(markets, (source === 'hyperliquid' ? 'polymarket' : source) as 'polymarket' | 'kalshi' | 'predict');
        const prefetchPromise = timeFilter === 'ALL' ? prefetchCache.get(prefetchKey) : null;
        const priceMap = new Map<number, Record<string, number | null>>();

        if (source === 'predict') {
          // Predict.fun — fetch real timeseries from GraphQL via backend
          const slug = eventSlug || markets[0]?.slug;
          const isExpanded = effectiveMarkets.length > markets.length;
          if (slug) {
            const periodMap: Record<TimeFilter, string> = { '1H': '24h', '6H': '24h', '1D': '24h', '1W': '7d', '1M': '30d', 'ALL': 'all' };
            const period = periodMap[timeFilter] || '7d';
            try {
              const res = await fetchWithTimeout(`/api/markets/predict/${encodeURIComponent(slug)}/timeseries?period=${period}`, {
                signal: controller.signal,
              });
              if (res.ok) {
                const data = await res.json();
                const timeseries: any[] = data.timeseries || [];

                if (isExpanded && timeseries.length === 1 && effectiveMarkets.length >= 2) {
                  // Single market expanded into N outcomes — first outcome uses the timeseries,
                  // remaining outcomes are synthesized as (100 - y) for binary markets
                  const series = timeseries[0];
                  (series.data || []).forEach((point: { x: number; y: number }) => {
                    if (point.y == null) return;
                    const existing = priceMap.get(point.x) || {};
                    existing[`market_0`] = point.y;
                    if (effectiveMarkets.length === 2) {
                      existing[`market_1`] = 100 - point.y;
                    }
                    priceMap.set(point.x, existing);
                  });
                } else {
                  // Multiple real markets — match by marketId as before
                  const marketIdToIndex = new Map<string, number>();
                  effectiveMarkets.forEach((m, i) => marketIdToIndex.set(m.id, i));
                  timeseries.forEach((series: any) => {
                    const idx = marketIdToIndex.get(series.marketId) ?? marketIdToIndex.get(series.title);
                    if (idx == null) return;
                    (series.data || []).forEach((point: { x: number; y: number }) => {
                      if (point.y == null) return;
                      const existing = priceMap.get(point.x) || {};
                      existing[`market_${idx}`] = point.y;
                      priceMap.set(point.x, existing);
                    });
                  });
                }
              }
            } catch (e: any) {
              if (e.name === 'AbortError') return;
            }
          }
          // Fallback: if no timeseries data, use current prices as flat line
          if (priceMap.size === 0) {
            const now = Math.floor(Date.now() / 1000);
            effectiveMarkets.forEach((market, index) => {
              const price = (market.outcomePrices?.[0] || 0.5) * 100;
              const point1 = priceMap.get(now - 3600) || {};
              point1[`market_${index}`] = price;
              priceMap.set(now - 3600, point1);
              const point2 = priceMap.get(now) || {};
              point2[`market_${index}`] = price;
              priceMap.set(now, point2);
            });
          }
        } else if (source === 'kalshi') {
          const allTickers = markets.map(m => m.id).filter(Boolean);
          if (allTickers.length === 0) { setInitialLoading(false); return; }

          const kalshiFilter = KALSHI_TIME_MAP[timeFilter];
          const now = Math.floor(Date.now() / 1000);
          const startTs = now - kalshiFilter.duration;

          const allResults: Record<string, any> = {};
          try {
            if (prefetchPromise) {
              Object.assign(allResults, await prefetchPromise);
            } else {
              const response = await fetchWithTimeout('/api/markets/kalshi/candlesticks/batch', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tickers: allTickers.slice(0, 20), period: kalshiFilter.period, startTs, endTs: now }),
                signal: controller.signal,
              });
              if (response.ok) {
                const result = await response.json();
                Object.assign(allResults, result.results || {});
              }
            }
          } catch (e: any) {
            if (e.name === 'AbortError') return;
          }

          markets.forEach((market, index) => {
            if (typeof market.id !== "string") return;
            const tickerKey = market.id.toUpperCase();
            const tickerData = allResults[tickerKey];
            if (!tickerData?.candlesticks) return;
            tickerData.candlesticks.forEach((c: { timestamp: number; close: number }) => {
              if (c.close == null || typeof c.close !== 'number' || isNaN(c.close)) return;
              const existing = priceMap.get(c.timestamp) || {};
              existing[`market_${index}`] = c.close * 100;
              priceMap.set(c.timestamp, existing);
            });
          });

          // Price-level forecast: collapse N market lines → 1 expected price line
          if (priceLevelInfo.isPriceLevel) {
            const forecastMap = new Map<number, Record<string, number | null>>();
            const minStrikes = priceLevelInfo.strikes.length * 0.5;
            for (const [timestamp, prices] of priceMap.entries()) {
              let sum = 0;
              let count = 0;
              for (const { marketIndex } of priceLevelInfo.strikes) {
                const val = prices[`market_${marketIndex}`];
                if (val != null) { sum += val / 100; count++; } // val is percentage, convert to 0-1
              }
              if (count >= minStrikes) {
                forecastMap.set(timestamp, { forecast: priceLevelInfo.baseStrike + sum });
              }
            }
            priceMap.clear();
            for (const [ts, data] of forecastMap) priceMap.set(ts, data);
          }
        } else {
          const filter = TIME_FILTERS.find(f => f.label === timeFilter);
          if (!filter) return;

          const tokenIds = markets.map(m => m.clobTokenIds?.[0]).filter((id): id is string => !!id);
          if (tokenIds.length === 0) { setInitialLoading(false); return; }

          let histories: Record<string, any[]> = {};
          if (prefetchPromise) {
            histories = await prefetchPromise;
          } else {
            const BATCH_LIMIT = 50;
            const chunks: string[][] = [];
            for (let i = 0; i < tokenIds.length; i += BATCH_LIMIT) {
              chunks.push(tokenIds.slice(i, i + BATCH_LIMIT));
            }
            const chunkResults = await Promise.all(
              chunks.map(async (chunk) => {
                try {
                  const response = await fetchWithTimeout('/api/polymarket/prices-history-batch', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ tokenIds: chunk, interval: filter.interval, fidelity: filter.fidelity }),
                    signal: controller.signal,
                  });
                  if (!response.ok) return {};
                  const result = await response.json();
                  return result.data?.histories || {};
                } catch (e: any) {
                  if (e.name === 'AbortError') throw e;
                  return {};
                }
              })
            );
            for (const chunk of chunkResults) {
              Object.assign(histories, chunk);
            }
          }

          markets.forEach((market, index) => {
            const tokenId = market.clobTokenIds?.[0];
            const history = tokenId ? (histories[tokenId] || []) : [];
            history.forEach((point: { time: number; price: number }) => {
              if (point.price == null || typeof point.price !== 'number' || isNaN(point.price)) return;
              const existing = priceMap.get(point.time) || {};
              existing[`market_${index}`] = point.price * 100;
              priceMap.set(point.time, existing);
            });
          });
        }

        if (controller.signal.aborted) return;

        const dataPoints = Array.from(priceMap.entries())
          .map(([timestamp, prices]) => ({ timestamp, ...prices }))
          .sort((a, b) => a.timestamp - b.timestamp);

        setChartData(dataPoints);
        hasLoadedOnce.current = true;
      } catch (e: any) {
        if (e.name === 'AbortError') return;
        setChartData([]);
      } finally {
        if (!controller.signal.aborted) {
          setInitialLoading(false);
          setUpdating(false);
        }
      }
    }

    fetchAllPriceHistories();
    return () => controller.abort();
  // Depend on stable market id keys, not the array refs themselves —
  // eventData re-polls every ~30s and creates new market arrays even when
  // contents are unchanged, which previously triggered an infinite refetch
  // loop on Kalshi sport pages.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markets.map((m) => m.id).join(','), effectiveMarkets.map((m) => m.id).join(','), timeFilter, source, isForecast]);

  // ─── Create chart ONCE (only recreate on theme or market count change) ──

  const marketsLengthRef = useRef(effectiveMarkets.length);
  const chartFadeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartContainerRef.current) return;
    const container = chartContainerRef.current;

    // Destroy previous chart if exists
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
      seriesRefs.current.clear();
    }

    const chart = createChart(container, {
      width: container.clientWidth,
      height: CHART_HEIGHT,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: isDark ? '#6B7280' : '#9CA3AF',
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: 11,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: {
          color: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)',
          style: 2,
        },
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: {
          color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.25)',
          width: 1,
          style: 3,
          labelVisible: false,
        },
        horzLine: {
          // Hide the horizontal line — PM's event chart doesn't show it, and
          // with per-series pills the horizontal ruler just adds visual noise.
          visible: false,
          labelVisible: false,
        },
      },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: { top: 0.08, bottom: 0.08 },
      },
      timeScale: {
        borderVisible: false,
        timeVisible: false,
        secondsVisible: false,
        fixLeftEdge: true,
        fixRightEdge: true,
        // Small gap after the last data point so the pulse dot and the
        // per-series price label don't sit on the same pixel column. Works
        // with fixRightEdge: true (reserves space, doesn't enable scroll).
        rightOffset: 12,
      },
      handleScroll: false,
      handleScale: false,
    });

    chartRef.current = chart;
    marketsLengthRef.current = effectiveMarkets.length;

    // Pre-create line series
    if (isForecast && priceLevelInfo.isPriceLevel) {
      // Single forecast line for price-level events
      const color = '#3B82F6';
      const series = chart.addSeries(LineSeries, {
        color,
        lineWidth: 3,
        lineType: LineType.Curved,
        visible: true,
        crosshairMarkerVisible: true,
        crosshairMarkerRadius: 5,
        crosshairMarkerBorderWidth: 2,
        crosshairMarkerBorderColor: isDark ? '#18181D' : '#ffffff',
        crosshairMarkerBackgroundColor: color,
        lastPriceAnimation: LastPriceAnimationMode.Continuous,
        priceLineVisible: false,
        title: 'Expected Price',
        priceFormat: {
          type: 'custom' as const,
          minMove: 0.01,
          formatter: (price: number) => { const p = Number(price ?? 0); return priceLevelInfo.hasDollarSign ? `$${p.toFixed(2)}` : p.toFixed(2); },
        },
      });
      seriesRefs.current.set(0, series);
    } else {
      // Standard multi-line chart
      effectiveMarkets.forEach((market, i) => {
        const color = OUTCOME_COLORS[i % OUTCOME_COLORS.length];

        const series = chart.addSeries(LineSeries, {
          color,
          lineWidth: 2,
          lineType: LineType.Curved,
          visible: visibleMarkets.has(i),
          crosshairMarkerVisible: true,
          crosshairMarkerRadius: 5,
          crosshairMarkerBorderWidth: 2,
          crosshairMarkerBorderColor: isDark ? '#18181D' : '#ffffff',
          crosshairMarkerBackgroundColor: color,
          lastPriceAnimation: LastPriceAnimationMode.Continuous,
          priceLineVisible: false,
          // Keep lastValueVisible — in this lightweight-charts build it's
          // tied to BOTH the pulsing dot and the axis tick labels. Hiding it
          // kills the pulses and blanks the % ticks. With COLLAPSED_LIMIT = 4
          // the per-series labels no longer stack badly.
          lastValueVisible: true,
        });

        seriesRefs.current.set(i, series);
      });
    }

    // Resize observer
    const ro = new ResizeObserver(entries => {
      const { width } = entries[0].contentRect;
      if (width > 0 && chartRef.current) {
        chartRef.current.resize(width, CHART_HEIGHT);
      }
    });
    ro.observe(container);

    // PM-style crosshair subscription.
    //
    // Critical nuance: `param.seriesData` only populates for series whose
    // data timestamps hit *exactly* `param.time`. In our merged priceMap,
    // each market's history has its own timestamps and they don't perfectly
    // align — so reading from seriesData produced only 1 pill per cursor.
    // Instead we look up the nearest-left timestamp in the full merged
    // chartData (via ref so the handler doesn't re-subscribe), then read
    // every visible market's value there with carry-forward. That gives one
    // pill per visible outcome, matching Polymarket's UX.
    const handleCrosshair = (param: any) => {
      if (!param || !param.point || param.point.x < 0 || param.point.y < 0) {
        setHoverState(null);
        return;
      }
      const t = typeof param.time === 'number' ? param.time : Number(param.time);
      if (!Number.isFinite(t)) {
        setHoverState(null);
        return;
      }
      const latestChartData = chartDataRef.current;
      if (!latestChartData || latestChartData.length === 0) {
        setHoverState(null);
        return;
      }
      // Binary-search-ish: pick the datapoint closest to the cursor time,
      // biased to at-or-before so we carry-forward instead of peeking ahead.
      let bestIdx = 0;
      let bestDt = Math.abs((latestChartData[0].timestamp as number) - t);
      for (let i = 1; i < latestChartData.length; i++) {
        const dt = Math.abs((latestChartData[i].timestamp as number) - t);
        if (dt < bestDt) { bestDt = dt; bestIdx = i; }
      }
      const snapshot = latestChartData[bestIdx];

      const currentMarkets = effectiveMarketsRef.current;
      const currentVisible = visibleMarketsRef.current;
      const items: Array<{ idx: number; y: number; name: string; color: string; value: number }> = [];

      seriesRefs.current.forEach((series, idx) => {
        if (!currentVisible.has(idx)) return;
        const market = currentMarkets[idx];
        if (!market) return;
        // Read value at snapshot; if missing (market has a gap at this ts),
        // walk backwards to the most recent known value.
        let value: number | undefined = (snapshot as any)[`market_${idx}`];
        if (typeof value !== 'number' || !Number.isFinite(value)) {
          for (let i = bestIdx - 1; i >= 0; i--) {
            const v = (latestChartData[i] as any)[`market_${idx}`];
            if (typeof v === 'number' && Number.isFinite(v)) { value = v; break; }
          }
        }
        if (typeof value !== 'number' || !Number.isFinite(value)) return;
        const yCoord = series.priceToCoordinate(value);
        if (yCoord == null) return;
        items.push({
          idx,
          y: yCoord,
          name: getShortLabel(market),
          color: OUTCOME_COLORS[idx % OUTCOME_COLORS.length],
          value,
        });
      });

      if (items.length === 0) {
        setHoverState(null);
        return;
      }
      items.sort((a, b) => b.value - a.value);
      setHoverState({
        x: param.point.x,
        time: (snapshot.timestamp as number) || t,
        items,
      });
    };
    chart.subscribeCrosshairMove(handleCrosshair);

    return () => {
      ro.disconnect();
      chart.unsubscribeCrosshairMove(handleCrosshair);
      chart.remove();
      chartRef.current = null;
      seriesRefs.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDark, effectiveMarkets.length, isForecast]);

  // ─── Update data WITHOUT recreating the chart ───────────────────────────

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || chartData.length === 0) return;

    // Fade out before updating
    if (chartFadeRef.current) {
      chartFadeRef.current.style.opacity = '0.3';
    }

    // Update timeScale visibility per filter
    const showTime = timeFilter === '1H' || timeFilter === '6H' || timeFilter === '1D';
    chart.timeScale().applyOptions({ timeVisible: showTime });

    // Update series data
    if (isForecast) {
      const series = seriesRefs.current.get(0);
      if (series) {
        const seriesData: { time: Time; value: number }[] = [];
        for (const point of chartData) {
          if (point.forecast != null) {
            seriesData.push({ time: point.timestamp as Time, value: point.forecast });
          }
        }
        series.setData(seriesData);
      }
    } else {
      effectiveMarkets.forEach((_market, i) => {
        const series = seriesRefs.current.get(i);
        if (!series) return;

        const seriesData: { time: Time; value: number }[] = [];
        for (const point of chartData) {
          const v = point[`market_${i}`];
          if (v != null) {
            seriesData.push({ time: point.timestamp as Time, value: v });
          }
        }
        series.setData(seriesData);
      });
    }

    chart.timeScale().fitContent();

    // Fade in smoothly after data is set
    requestAnimationFrame(() => {
      if (chartFadeRef.current) {
        chartFadeRef.current.style.opacity = '1';
      }
    });
  }, [chartData, effectiveMarkets, timeFilter, isForecast]);

  // ─── Update series visibility ──────────────────────────────────────────

  useEffect(() => {
    seriesRefs.current.forEach((series, i) => {
      const isVisible = visibleMarkets.has(i);
      // lightweight-charts in this build keeps the pulse dot painting even
      // when `visible: false` / `lastValueVisible: false` — the animation
      // frame keeps firing because `lastPriceAnimation` is bound at creation
      // to Continuous. Flip the animation mode in lockstep with visibility
      // so hidden series stop pulsing entirely.
      series.applyOptions({
        visible: isVisible,
        lastValueVisible: isVisible,
        lastPriceAnimation: isVisible
          ? LastPriceAnimationMode.Continuous
          : LastPriceAnimationMode.Disabled,
      });
    });
  }, [visibleMarkets]);

  const toggleMarket = (index: number) => {
    setVisibleMarkets(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // Don't render for a SINGLE binary Yes/No market on Polymarket — that case
  // already has its own chart on /market/[slug]. Multi-binary events (2+
  // markets each with Yes/No outcomes, e.g. "Iran by Jan / Feb / Mar") still
  // get the multi-line chart so users can compare strikes.
  // Binary markets with Yes/No outcomes — still show the chart on event page

  const hasData = chartData.length > 0;

  return (
    <div>
      {/* Header */}
      <div className="pt-2 pb-2">
        {/* Legend pills */}
        <HorizontalScroll ref={pillsRef} onMouseDown={handlePillsMouseDown} fadeFrom="bg-white dark:bg-[#18181D]" className="flex items-center gap-1.5 px-5 cursor-grab select-none">
          {initialLoading ? (
            <>
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5">
                  <div className="w-2.5 h-2.5 rounded-[5px] bg-gray-200 dark:bg-gray-800 animate-pulse" />
                  <div className="h-3 w-14 rounded bg-gray-200 dark:bg-gray-800 animate-pulse" />
                </div>
              ))}
            </>
          ) : isForecast && priceLevelInfo.isPriceLevel ? (
            <div className="flex items-center gap-2 px-2.5 py-1.5">
              <div className="w-2.5 h-2.5 rounded-[5px] flex-shrink-0" style={{ backgroundColor: '#3B82F6', boxShadow: '0 0 6px #3B82F640' }} />
              <span className="text-gray-700 dark:text-gray-200 font-medium text-[11px]">Expected Price</span>
              {liveForecast != null && (
                <span className="font-bold text-gray-900 dark:text-white tabular-nums text-[11px]">
                  {priceLevelInfo.hasDollarSign ? '$' : ''}{toSafeNumber(liveForecast).toFixed(2)}
                </span>
              )}
            </div>
          ) : effectiveMarkets.length <= 1 ? null : (
            <>
              {displayedIndices.map((index) => {
                const market = effectiveMarkets[index];
                const price = market.outcomePrices?.[0] || 0;
                const isVisible = visibleMarkets.has(index);
                const color = OUTCOME_COLORS[index % OUTCOME_COLORS.length];
                return (
                  <button
                    key={market.id}
                    onClick={() => toggleMarket(index)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-[5px] text-[11px] font-inter-tight transition-colors flex-shrink-0 ${
                      isVisible
                        ? "bg-gray-50 dark:bg-[#0f0f12]"
                        : "opacity-40 hover:opacity-60"
                    }`}
                  >
                    <div
                      className="w-2 h-2 rounded-[5px] flex-shrink-0"
                      style={{ backgroundColor: color, boxShadow: isVisible ? `0 0 6px ${color}40` : 'none' }}
                    />
                    <span className="text-gray-700 dark:text-gray-200 font-medium truncate max-w-[100px]">{getShortLabel(market)}</span>
                    <span className="font-bold text-gray-900 dark:text-white tabular-nums flex-shrink-0">
                      {formatCents(price)}
                    </span>
                  </button>
                );
              })}
              {hiddenCount > 0 && (
                <button
                  onClick={() => setIsLegendExpanded(!isLegendExpanded)}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-[5px] text-[11px] font-inter-tight font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors flex-shrink-0"
                >
                  {isLegendExpanded ? t("chartLegend.showLess") : t("chartLegend.showMore", { n: hiddenCount })}
                </button>
              )}
            </>
          )}
        </HorizontalScroll>
      </div>

      {/* Chart area */}
      <div className="px-2 pb-3">
        <div className="relative">
          {/* Initial loading skeleton */}
          {initialLoading && (
            <div className="h-[200px] relative overflow-hidden px-3">
              <div className="absolute left-3 top-0 bottom-8 w-10 flex flex-col justify-between py-4">
                {[100, 75, 50, 25, 0].map(v => (
                  <div key={v} className="h-3 w-8 rounded bg-gray-100 dark:bg-white/[0.04] animate-pulse" />
                ))}
              </div>
              <div className="ml-14 h-[200px] relative">
                {[0, 1, 2, 3, 4].map(i => (
                  <div key={i} className="absolute w-full border-t border-gray-100 dark:border-white/[0.04]" style={{ top: `${i * 25}%` }} />
                ))}
                <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                  {[
                    { d: "M0,40 C15,38 35,45 50,42 S85,48 100,44", color: "#3B82F6" },
                    { d: "M0,60 C20,55 40,65 55,58 S80,68 100,62", color: "#F59E0B" },
                    { d: "M0,78 C15,74 30,80 50,76 S75,82 100,79", color: "#8B5CF6" },
                  ].map((line, i) => (
                    <path key={i} d={line.d} fill="none" stroke={line.color} strokeWidth="2" opacity="0.12" vectorEffect="non-scaling-stroke" className="animate-pulse" style={{ animationDelay: `${i * 200}ms` }} />
                  ))}
                </svg>
              </div>
              <div className="ml-14 flex justify-between mt-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-3 w-10 rounded bg-gray-100 dark:bg-white/[0.04] animate-pulse" style={{ animationDelay: `${i * 100}ms` }} />
                ))}
              </div>
            </div>
          )}

          {/* Updating overlay */}
          {updating && !initialLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-[#18181D]/50 rounded-[5px] backdrop-blur-[2px]">
              <div className="w-6 h-6 border-2 border-gray-200 dark:border-gray-700 border-t-gray-900 dark:border-t-white rounded-full animate-spin" />
            </div>
          )}

          {/* Empty state */}
          {!initialLoading && !hasData && (
            <div className="h-[200px] flex flex-col items-center justify-center gap-2">
              <FiActivity className="w-5 h-5 text-gray-300 dark:text-gray-600" />
              <p className="font-inter-tight text-sm text-gray-400 dark:text-gray-500">No trading data available</p>
            </div>
          )}

          {/* Chart container — visx for Kalshi, lightweight-charts for others.
              Kalshi renders a pulsing dot at the latest data point on the
              right edge; we use overflow:visible so the ping ring isn't clipped. */}
          <div className="relative" style={{
            height: hasData && !initialLoading ? CHART_HEIGHT : 0,
            overflow: source === 'kalshi' ? 'visible' : 'hidden',
          }}>
            {source === 'kalshi' && hasData && !initialLoading ? (
              <KalshiVisxChart
                chartData={chartData}
                effectiveMarkets={effectiveMarkets}
                visibleMarkets={visibleMarkets}
                isDark={isDark}
                height={CHART_HEIGHT}
                isForecast={isForecast}
                forecastHasDollarSign={priceLevelInfo.isPriceLevel ? priceLevelInfo.hasDollarSign : false}
              />
            ) : (
              <div
                ref={chartFadeRef}
                style={{ width: '100%', height: '100%', opacity: hasData && !initialLoading ? 1 : 0, transition: 'opacity 350ms ease-out' }}
              >
                <div ref={chartContainerRef} style={{ width: '100%', height: '100%' }} />
                {/* PM-style hover overlay — one pill per visible outcome anchored
                    to its line intersection, a date label at the top of the
                    cursor, and a subtle gray veil over the "future" side. */}
                {hoverState && hoverState.items.length > 0 && (() => {
                  const containerW = chartContainerRef.current?.clientWidth ?? 0;
                  // Pills flip to the LEFT when the cursor is past the
                  // chart midpoint so they never get clipped by the right edge.
                  const flipX = hoverState.x > containerW / 2;
                  const d = new Date(hoverState.time * 1000);
                  const needsTime = timeFilter === '1H' || timeFilter === '6H' || timeFilter === '1D';
                  const dateLabel = needsTime
                    ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  return (
                    <>
                      {/* Future gray veil — right of the cursor. */}
                      <div
                        className="absolute pointer-events-none"
                        style={{
                          left: hoverState.x,
                          right: 0,
                          top: 0,
                          bottom: 0,
                          background: isDark ? 'rgba(24,24,29,0.45)' : 'rgba(255,255,255,0.55)',
                          zIndex: 5,
                        }}
                      />
                      {/* Date label at the top of the cursor column. */}
                      <div
                        className="absolute z-20 pointer-events-none text-[11px] text-gray-500 dark:text-gray-400 font-satoshi whitespace-nowrap"
                        style={{
                          left: hoverState.x,
                          top: -2,
                          transform: 'translateX(-50%)',
                        }}
                      >
                        {dateLabel}
                      </div>
                      {/* One solid pill per outcome, anchored to its series line. */}
                      {hoverState.items.map((it) => (
                        <div
                          key={it.idx}
                          className="absolute z-20 pointer-events-none flex items-center gap-1.5 px-2 py-0.5 rounded-[5px] text-[11px] font-semibold whitespace-nowrap shadow-sm"
                          style={{
                            left: hoverState.x + (flipX ? -10 : 10),
                            top: it.y,
                            transform: `translateY(-50%) ${flipX ? 'translateX(-100%)' : ''}`,
                            backgroundColor: it.color,
                            color: '#fff',
                          }}
                        >
                          <span className="truncate max-w-[160px]">{it.name}</span>
                          <span className="tabular-nums">{it.value.toFixed(1)}%</span>
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Chart meta-footer — Polymarket-style. Left: total volume, close
            date, optional "Earn 4%" Holding Rewards badge. Right: time range
            pills. Shrinks gracefully on mobile (icons stay, labels wrap). */}
        <div className="flex flex-col-reverse md:flex-row items-start md:items-center justify-between gap-2 md:gap-3 px-5 py-2 md:flex-wrap">
          <div className="flex items-center gap-4 text-[13px] font-medium whitespace-nowrap text-gray-600 dark:text-gray-400">
            {typeof eventVolume === "number" && eventVolume > 0 && (
              <span className="inline-flex items-center gap-1.5" title={t("chartMeta.totalVolume")}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                  <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                  <path d="M4 22h16" />
                  <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
                  <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
                  <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
                </svg>
                <span className="tabular-nums">
                  ${eventVolume >= 1_000_000
                    ? (eventVolume / 1_000_000).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "M"
                    : eventVolume >= 1_000
                      ? (eventVolume / 1_000).toLocaleString("en-US", { maximumFractionDigits: 1 }) + "K"
                      : eventVolume.toLocaleString("en-US", { maximumFractionDigits: 0 })} {t("chartMeta.volSuffix")}
                </span>
              </span>
            )}
            {eventEndDate && (() => {
              const d = new Date(eventEndDate);
              if (!Number.isFinite(d.getTime())) return null;
              const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
              return (
                <span className="inline-flex items-center gap-1.5" title={t("chartMeta.resolutionDate")}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  {label}
                </span>
              );
            })()}
            {/* Holding Rewards badge — gated on Gamma's
                `markets[i].holdingRewardsEnabled`. Rate is a platform-wide
                constant (same 4% Polymarket shows) so it's safe to hardcode.
                Hover tooltip explains + links to Polymarket's help center. */}
            {hasHoldingRewards && (
              <span className="group relative inline-flex items-center gap-1.5 text-yellow-700 dark:text-yellow-500 cursor-default">
                <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="m8.5,8.051c1.597-.525,2.75-2.028,2.75-3.801v-.5h-1.75c-1.933,0-3.5,1.567-3.5,3.5v1" />
                  <line x1="6" y1="11.25" x2="6" y2="6.25" />
                  <path d="m.75,1.75h1.75c1.932,0,3.5,1.568,3.5,3.5v1h-1.25c-2.208,0-4-1.792-4-4v-.5h0Z" />
                </svg>
                <span>{t("chartMeta.earn")}</span>
                <span
                  role="tooltip"
                  className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 w-64 max-w-[calc(100vw-2rem)] whitespace-normal opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity bg-neutral-900 text-white text-[11px] leading-5 text-center font-medium rounded-[5px] py-2 px-3 z-50 shadow-lg border border-white/10"
                >
                  {t("chartMeta.earnTooltip")}{" "}
                  <a
                    href="https://help.polymarket.com/en/articles/13364459-holding-rewards"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-blue-300 hover:brightness-110 pointer-events-auto"
                  >
                    {t("chartMeta.learnMore")}
                  </a>
                </span>
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 p-0.5 bg-gray-100 dark:bg-[#0f0f12] rounded-[5px]">
            {TIME_FILTERS.map(({ label }) => (
              <button
                key={label}
                onClick={() => setTimeFilter(label)}
                className={`px-3 py-1.5 rounded-[5px] text-xs font-inter-tight font-medium transition-colors ${
                  timeFilter === label
                    ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
